/**
 * Booking session JWT for AzDance public APIs (bootstrap `POST …/booking/participants` / login).
 * Send only `X-AzDance-Booking-Token` (backend contract).
 */
const COOKIE_NAME = 'azdance_booking_token';
/** Aligned with backend `AzdanceBookingParticipantTokenService` (`365d`). */
const MAX_AGE_SEC = 60 * 60 * 24 * 365;

const bookingTokenListeners = new Set<() => void>();

function emitBookingTokenChange(): void {
  for (const listener of bookingTokenListeners) {
    try {
      listener();
    } catch {
      // ignore broken subscribers
    }
  }
}

/** Notify `useAzDanceBookingToken` subscribers (same tab). */
export function notifyAzDanceBookingTokenChange(): void {
  emitBookingTokenChange();
}

/** React `useSyncExternalStore` / effects: re-run when booking cookie changes (same tab). */
export function subscribeAzDanceBookingToken(onStoreChange: () => void): () => void {
  bookingTokenListeners.add(onStoreChange);
  return () => {
    bookingTokenListeners.delete(onStoreChange);
  };
}

const BOOKING_EVENT_ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

function cookieAttrs(maxAgeSec: number): string {
  const secure =
    typeof window !== 'undefined' && window.location?.protocol === 'https:'
      ? '; Secure'
      : '';
  return `path=/; SameSite=Lax; max-age=${maxAgeSec}${secure}`;
}

export function getAzDanceBookingToken(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(
    new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`),
  );
  const raw = m?.[1];
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function setAzDanceBookingToken(token: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; ${cookieAttrs(MAX_AGE_SEC)}`;
  emitBookingTokenChange();
  // Lazy import avoids circular deps with api-client → azdanceBookingAuth.
  void import('@/lib/azdanceBookingTokenSync').then((m) =>
    m.markAzDanceBookingTokenSynced(token),
  );
}

export function clearAzDanceBookingToken(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=; ${cookieAttrs(0)}`;
  emitBookingTokenChange();
  void import('@/lib/azdanceBookingTokenSync').then((m) =>
    m.markAzDanceBookingTokenSynced(null),
  );
}

export function azdanceBookingAuthHeaders(
  token?: string | null,
): Record<string, string> {
  const t = token ?? getAzDanceBookingToken();
  if (!t) return {};
  return {
    'X-AzDance-Booking-Token': t,
  };
}

export type BookingJwtPayload = {
  sub: string;
  exp?: number;
  /** `AzDanceEventBooking.id` when issued with an attached booking (same as API `eventBookingId`). */
  eventBookingId?: string;
};

/** Decode booking JWT payload (no signature verification). */
export function decodeBookingJwtPayload(token: string): BookingJwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    if (typeof atob !== 'function') return null;
    const json = JSON.parse(atob(padded)) as {
      sub?: string;
      exp?: number;
      eventBookingId?: string;
    };
    const sub = typeof json.sub === 'string' ? json.sub.trim() : '';
    if (!sub) return null;
    const exp = typeof json.exp === 'number' ? json.exp : undefined;
    const eb =
      typeof json.eventBookingId === 'string'
        ? json.eventBookingId.trim()
        : undefined;
    const out: BookingJwtPayload = { sub, exp };
    if (eb) out.eventBookingId = eb;
    return out;
  } catch {
    return null;
  }
}

/** `eventBookingId` from JWT when present and looks like a Crockford ULID. */
export function extractEventBookingIdFromBookingJwt(
  token: string,
): string | null {
  const eid = decodeBookingJwtPayload(token)?.eventBookingId?.trim();
  if (eid && BOOKING_EVENT_ULID_RE.test(eid)) return eid;
  return null;
}

/**
 * Есть кука `azdance_booking_token`, парсится как JWT, есть `sub`, и при наличии `exp` токен ещё не истёк.
 * Это «авторизация» registration-flow / корзины, даже без `currentUser` в Zustand.
 */
export function isAzDanceBookingTokenValid(): boolean {
  const token = getAzDanceBookingToken();
  if (!token) return false;
  const payload = decodeBookingJwtPayload(token);
  if (!payload) return false;
  if (payload.exp != null) {
    const skewMs = 30_000;
    return payload.exp * 1000 > Date.now() - skewMs;
  }
  return true;
}

/** Read JWT payload `sub` (primary participant id) without verifying signature. */
export function decodeJwtSub(token: string): string | null {
  return decodeBookingJwtPayload(token)?.sub ?? null;
}
